import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { ProducerSectionHeader } from '@/components/producer/ProducerSectionHeader';
import { AlertCircle, CheckCircle, Clock, FileText, Loader2, Mail, Phone, Scale, Send, ShieldCheck } from 'lucide-react';
import { useProducerById, useUpdateProducer } from '@/hooks/useMyProducers';
import { useProducerTerms } from '@/hooks/useProducerTerms';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { getVerificationStatus } from '@/data/producers/types';
import { logAuditEvent } from '@/lib/logging/auditLog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useSaveFlow } from '@/hooks/useSaveFlow';
import { SaveButton } from '@/components/ui/SaveButton';

const LEGAL_TYPE_OPTIONS = [
  { value: 'company', label: 'Aktiebolag' },
  { value: 'sole_trader', label: 'Enskild firma' },
  { value: 'association', label: 'Förening' },
  { value: 'individual', label: 'Privatperson (begränsad)' },
] as const;

const ORG_NUMBER_REQUIRED_TYPES = ['company', 'sole_trader', 'association'];

function requiresOrgNumber(legalType: string | null): boolean {
  return !!legalType && ORG_NUMBER_REQUIRED_TYPES.includes(legalType);
}

export default function ProducerVerificationPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const updateMutation = useUpdateProducer();
  const { hasCapability } = useProducerAccess(farmId !== 'demo' ? farmId : undefined);
  const {
    isAccepted: termsAccepted,
    acceptedVersion,
    termsStatus,
    requiredTitle,
  } = useProducerTerms(producer?.id);

  const contactSectionRef = useRef<HTMLDivElement>(null);

  const [legalType, setLegalType] = useState<string | null>(null);
  const [orgNumber, setOrgNumber] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isContactDirty, setIsContactDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync local state from producer when loaded
  const [initialized, setInitialized] = useState(false);
  if (producer && !initialized) {
    setLegalType(producer.legal_type || null);
    setOrgNumber(producer.org_number || '');
    setContactPhone(producer.phone || '');
    setContactEmail(producer.email || '');
    setInitialized(true);
  }

  const canEdit = hasCapability('canEditProducerInfo');

  const verificationStatus = producer
    ? getVerificationStatus({
        verified: producer.verified ?? false,
        verification_submitted_at: producer.verification_submitted_at,
      })
    : 'not_submitted';

  const hasContact = !!contactPhone.trim() || !!contactEmail.trim();

  // ── Missing fields for verification ──
  function getMissingFields(): { label: string; anchor?: string }[] {
    const missing: { label: string; anchor?: string }[] = [];
    if (!producer?.name?.trim()) missing.push({ label: 'Värdnamn' });
    if (!legalType) missing.push({ label: 'Juridisk form' });
    if (requiresOrgNumber(legalType) && !orgNumber.trim()) missing.push({ label: 'Organisationsnummer' });
    if (!hasContact) missing.push({ label: 'Kontaktuppgift', anchor: 'contact' });
    if (!termsAccepted) missing.push({ label: 'Godkända villkor' });
    return missing;
  }

  const missingFields = getMissingFields();
  const canSubmitVerification = missingFields.length === 0 && verificationStatus === 'not_submitted';

  const scrollToContact = () => {
    contactSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Save legal info ──
  const { save: saveLegal, isSaving: isSavingLegal } = useSaveFlow(
    async () => {
      if (!producer) throw new Error('No producer');
      await updateMutation.mutateAsync({
        id: producer.id,
        updates: {
          legal_type: legalType,
          org_number: orgNumber.trim() || null,
        } as any,
      });
      await logAuditEvent({
        entityType: 'producer',
        entityId: producer.id,
        action: 'legal_info_updated',
        message: 'Juridisk information uppdaterad',
        metadata: { legal_type: legalType, has_org_number: !!orgNumber.trim() },
      });
      setIsDirty(false);
    },
    { entity: 'producer', successMessage: 'Juridisk information uppdaterad' },
  );
  const handleSaveLegal = () => saveLegal();

  // ── Save contact info ──
  const { save: saveContact, isSaving: isSavingContact } = useSaveFlow(
    async () => {
      if (!producer) throw new Error('No producer');
      await updateMutation.mutateAsync({
        id: producer.id,
        updates: {
          phone: contactPhone.trim() || null,
          email: contactEmail.trim() || null,
        } as any,
      });
      await logAuditEvent({
        entityType: 'producer',
        entityId: producer.id,
        action: 'contact_info_updated',
        message: 'Kontaktuppgifter uppdaterade',
        metadata: { has_phone: !!contactPhone.trim(), has_email: !!contactEmail.trim() },
      });
      setIsContactDirty(false);
    },
    { entity: 'producer', successMessage: 'Kontaktuppgifter uppdaterade' },
  );
  const handleSaveContact = () => saveContact();

  // ── Submit for verification ──
  const handleSubmitVerification = async () => {
    if (!canSubmitVerification || isSubmitting || !producer) return;

    if (isDirty) await handleSaveLegal();
    if (isContactDirty) await handleSaveContact();

    setIsSubmitting(true);
    try {
      const { error } = await supabase.rpc('submit_producer_verification', {
        p_producer_id: producer.id,
      });
      if (error) throw error;

      toast({
        title: 'Inskickad för verifiering ✅',
        description: 'Vi granskar din verksamhet och återkommer inom kort.',
      });
      refetch();
    } catch (err: any) {
      console.error('Submit verification error:', err);

      await logAuditEvent({
        entityType: 'producer',
        entityId: producer.id,
        action: 'blocked',
        message: 'Verifieringsförsök misslyckades',
        metadata: { error: err.message },
      });

      toast({
        title: 'Något gick fel',
        description: err.message?.includes('already submitted')
          ? 'Du har redan skickat in.'
          : 'Kunde inte skicka. Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Loading / Error states ──
  if (isLoading) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Verifiering & juridik">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error || !producer) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Verifiering & juridik">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Kunde inte ladda gården.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">Försök igen</Button>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout
      farmId={farmId || 'demo'}
      producerName={producer.name || 'Din gård'}
      title="Verifiering & juridik"
    >
      <ProducerSectionHeader
        title="Verifiering & juridik"
        description="Juridisk information, kontakt, avtal och verifieringsstatus."
      />

      <div className="space-y-6 mt-6">
        {/* ── Verification Status ── */}
        <VerificationStatusSection
          status={verificationStatus}
          missingFields={missingFields}
          canSubmit={canSubmitVerification}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmitVerification}
          onScrollToContact={scrollToContact}
        />

        <Separator />

        {/* ── Legal Information ── */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Juridisk information</CardTitle>
            </div>
            <CardDescription>
              Juridisk form och organisationsnummer för din verksamhet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="legal-type">Juridisk form</Label>
              <Select
                value={legalType || ''}
                onValueChange={(v) => {
                  setLegalType(v);
                  setIsDirty(true);
                }}
                disabled={!canEdit || verificationStatus === 'verified'}
              >
                <SelectTrigger id="legal-type">
                  <SelectValue placeholder="Välj juridisk form" />
                </SelectTrigger>
                <SelectContent>
                  {LEGAL_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {requiresOrgNumber(legalType) && (
              <div className="space-y-2">
                <Label htmlFor="org-number">Organisationsnummer</Label>
                <Input
                  id="org-number"
                  value={orgNumber}
                  onChange={(e) => {
                    setOrgNumber(e.target.value);
                    setIsDirty(true);
                  }}
                  placeholder="XXXXXX-XXXX"
                  disabled={!canEdit || verificationStatus === 'verified'}
                />
              </div>
            )}

            {canEdit && verificationStatus !== 'verified' && (
              <SaveButton
                onClick={handleSaveLegal}
                disabled={!isDirty}
                isSaving={isSavingLegal}
                size="sm"
              >
                Spara juridisk information
              </SaveButton>
            )}

            {verificationStatus === 'verified' && (
              <p className="text-sm text-muted-foreground">
                Juridisk information kan inte ändras efter verifiering. Kontakta Farmly vid behov.
              </p>
            )}
          </CardContent>
        </Card>

        <Separator />

        {/* ── Contact Information ── */}
        <div ref={contactSectionRef} id="contact">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Kontaktuppgifter (för verifiering)</CardTitle>
              </div>
              <CardDescription>
                Minst en kontaktuppgift (telefon eller e-post) krävs för verifiering.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact-phone" className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  Telefon
                </Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => {
                    setContactPhone(e.target.value);
                    setIsContactDirty(true);
                  }}
                  placeholder="070-123 45 67"
                  disabled={!canEdit}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-email" className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  E-post
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => {
                    setContactEmail(e.target.value);
                    setIsContactDirty(true);
                  }}
                  placeholder="kontakt@exempel.se"
                  disabled={!canEdit}
                />
              </div>

              {!hasContact && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  ⚠️ Minst en kontaktuppgift krävs för att kunna skicka in verifiering.
                </p>
              )}

              {canEdit && (
                <SaveButton
                  onClick={handleSaveContact}
                  disabled={!isContactDirty}
                  isSaving={isSavingContact}
                  size="sm"
                >
                  Spara kontaktuppgifter
                </SaveButton>
              )}
            </CardContent>
          </Card>
        </div>

        <Separator />

        {/* ── Terms / Agreement ── */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Avtal</CardTitle>
            </div>
            <CardDescription>
              Värdvillkor som gäller för din användning av Farmly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {termsAccepted ? (
              <div className="flex items-start gap-3 p-4 rounded-lg border border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20">
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium text-foreground">{requiredTitle}</p>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>Version: {acceptedVersion}</span>
                    <span>·</span>
                    <span>Godkänt: {termsStatus?.acceptedAt
                      ? new Date(termsStatus.acceptedAt).toLocaleDateString('sv-SE')
                      : '–'}</span>
                  </div>
                  <Badge variant="secondary" className="mt-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
                    Godkänt ✅
                  </Badge>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Villkor ej godkända
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Du behöver godkänna värdvillkoren för att kunna verifieras.
                    Villkoren visas automatiskt när du loggar in i portalen.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ProducerLayout>
  );
}

// ── Sub-component: Verification Status ──

function VerificationStatusSection({
  status,
  missingFields,
  canSubmit,
  isSubmitting,
  onSubmit,
  onScrollToContact,
}: {
  status: 'not_submitted' | 'pending' | 'verified';
  missingFields: { label: string; anchor?: string }[];
  canSubmit: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onScrollToContact: () => void;
}) {
  if (status === 'verified') {
    return (
      <Alert className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20">
        <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        <div className="ml-2">
          <div className="flex items-center gap-2 font-medium text-emerald-800 dark:text-emerald-200">
            Verifierad
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200">
              ✅ Godkänd
            </Badge>
          </div>
          <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
            Din verksamhet är verifierad. 
          </p>
        </div>
      </Alert>
    );
  }

  if (status === 'pending') {
    return (
      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
        <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <div className="ml-2">
          <div className="flex items-center gap-2 font-medium text-blue-800 dark:text-blue-200">
            Under granskning
            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
              Inskickad
            </Badge>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
            Vi granskar din verksamhet och återkommer inom kort.
            Du kan fortsätta förbereda din gårdsprofil under tiden.
          </p>
        </div>
      </Alert>
    );
  }

  // not_submitted
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Verifieringsstatus</CardTitle>
        </div>
        <CardDescription>
          Verifiering krävs för att kunna sälja via Farmly.
          Du behöver juridisk information och godkända villkor – inte en färdig profil.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Badge variant="outline">Ej inskickad</Badge>

        {missingFields.length > 0 && (
          <div className="text-sm space-y-1">
            <p className="font-medium text-muted-foreground">Följande saknas:</p>
            <ul className="list-disc list-inside text-muted-foreground">
              {missingFields.map((f) => (
                <li key={f.label}>
                  {f.anchor === 'contact' ? (
                    <button
                      type="button"
                      className="underline hover:text-foreground transition-colors"
                      onClick={onScrollToContact}
                    >
                      {f.label}
                    </button>
                  ) : (
                    f.label
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Button
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
          size="sm"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Skicka in för verifiering
        </Button>
      </CardContent>
    </Card>
  );
}
